import { z } from 'zod';

export const RoleSchema = z.enum([
  'super_admin',
  'workspace_admin',
  'engineer',
  'operator',
  'viewer',
]);

export const UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  display_name: z.string().min(1),
  avatar_url: z.string().nullable(),
  is_super_admin: z.boolean(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const MembershipSchema = z.object({
  id: z.string().min(1),
  user_id: z.string().min(1),
  workspace_id: z.string().min(1),
  role: RoleSchema,
  created_at_ms: z.number().int().nonnegative(),
  // Identity joined in from Firebase Auth (the API resolves uid → name/email)
  // so the members list can show a human label instead of a bare uid. Null
  // when the uid has no Auth record.
  display_name: z.string().nullable(),
  email: z.string().nullable(),
  // B1 console access flag — absent on older docs defaults to false.
  console: z.boolean().optional().default(false),
});

export const LoginRequestSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

export const LoginResponseSchema = z.object({
  token: z.string().min(1),
  user: UserSchema,
});

export const CurrentUserSchema = z.object({
  user: UserSchema,
  memberships: z.array(MembershipSchema),
});

export const CreateMembershipSchema = z.object({
  user_id: z.string().min(1),
  role: RoleSchema,
});

export const UpdateMembershipSchema = z.object({
  role: RoleSchema,
});

export const UpdateConsoleAccessSchema = z.object({
  console: z.boolean(),
});

export type ParsedRole = z.infer<typeof RoleSchema>;
export type ParsedUser = z.infer<typeof UserSchema>;
export type ParsedMembership = z.infer<typeof MembershipSchema>;
export type ParsedLoginRequest = z.infer<typeof LoginRequestSchema>;
export type ParsedLoginResponse = z.infer<typeof LoginResponseSchema>;
export type ParsedCurrentUser = z.infer<typeof CurrentUserSchema>;
export type ParsedCreateMembership = z.infer<typeof CreateMembershipSchema>;
export type ParsedUpdateMembership = z.infer<typeof UpdateMembershipSchema>;
export type ParsedUpdateConsoleAccess = z.infer<typeof UpdateConsoleAccessSchema>;
